// LOGOMAN PRO — object-shape container library.
// Each shape is SVG markup drawn in a 0..100 local viewBox. The renderer scales
// and translates it to fit the actual container size.

import type { ContainerShape } from "../state/logoStore";

export interface ContainerShapeDef {
  id: ContainerShape;
  name: string;
  /** SVG primitives in 0..100 coords. No fill — caller sets it via group. */
  body: string;
  /** Fill rule needed for shapes that punch holes (e.g. tag with hole). */
  fillRule?: "nonzero" | "evenodd";
}

/* eslint-disable max-len */
export const CONTAINER_SHAPES: Partial<Record<ContainerShape, ContainerShapeDef>> = {
  glasses: {
    id: "glasses",
    name: "Glasses",
    body: `
      <ellipse cx="26" cy="55" rx="22" ry="22"/>
      <ellipse cx="74" cy="55" rx="22" ry="22"/>
      <rect x="44" y="52" width="12" height="6" rx="1.5"/>
    `,
  },
  phone: {
    id: "phone",
    name: "Phone",
    body: `<rect x="28" y="6" width="44" height="88" rx="7" ry="7"/>`,
  },
  cupcake: {
    id: "cupcake",
    name: "Cupcake",
    body: `
      <path d="M 22 50 C 22 18, 78 18, 78 50 L 70 50 L 75 92 L 25 92 L 30 50 Z"/>
    `,
  },
  pizza: {
    id: "pizza",
    name: "Pizza slice",
    body: `<path d="M 50 6 L 92 92 A 56 56 0 0 0 8 92 Z"/>`,
  },
  computer: {
    id: "computer",
    name: "Computer",
    body: `
      <rect x="8" y="14" width="84" height="56" rx="4"/>
      <rect x="44" y="70" width="12" height="10"/>
      <rect x="22" y="80" width="56" height="8" rx="2"/>
    `,
  },
  bubble: {
    id: "bubble",
    name: "Speech",
    body: `
      <path d="M 12 14 L 88 14 Q 96 14 96 22 L 96 66 Q 96 74 88 74 L 60 74 L 48 92 L 50 74 L 12 74 Q 4 74 4 66 L 4 22 Q 4 14 12 14 Z"/>
    `,
  },
  cloud: {
    id: "cloud",
    name: "Cloud",
    body: `
      <path d="M 24 72 Q 4 72 8 54 Q 0 36 22 32 Q 28 16 48 22 Q 66 14 74 32 Q 96 32 92 52 Q 100 70 78 72 Z"/>
    `,
  },
  house: {
    id: "house",
    name: "House",
    body: `
      <path d="M 50 8 L 96 46 L 88 46 L 88 92 L 12 92 L 12 46 L 4 46 Z"/>
    `,
  },
  banner: {
    id: "banner",
    name: "Banner",
    body: `
      <path d="M 6 26 L 88 26 L 76 50 L 88 74 L 6 74 L 18 50 Z"/>
    `,
  },
  tag: {
    id: "tag",
    name: "Tag",
    body: `
      <path d="M 22 12 L 92 12 L 92 88 L 22 88 L 6 50 Z M 22 46 a 4 4 0 1 0 0.001 0 Z"/>
    `,
    fillRule: "evenodd",
  },
  cup: {
    id: "cup",
    name: "Coffee cup",
    body: `
      <path d="M 22 22 L 70 22 L 64 88 L 28 88 Z"/>
      <path d="M 70 34 Q 88 34 88 54 Q 88 74 70 74 L 70 68 Q 82 68 82 54 Q 82 40 70 40 Z"/>
    `,
  },
  lightbulb: {
    id: "lightbulb",
    name: "Lightbulb",
    body: `
      <path d="M 50 8 C 26 8, 22 46, 36 60 L 36 70 L 64 70 L 64 60 C 78 46, 74 8, 50 8 Z"/>
      <rect x="38" y="72" width="24" height="8" rx="1.5"/>
      <rect x="40" y="82" width="20" height="4" rx="1"/>
      <rect x="44" y="88" width="12" height="4" rx="1"/>
    `,
  },
  // Comic-book containers
  burst: {
    id: "burst",
    name: "Burst",
    body: `
      <polygon points="50,0 58,26 82,8 72,38 100,50 72,62 82,92 58,74 50,100 42,74 18,92 28,62 0,50 28,38 18,8 42,26"/>
    `,
  },
  "speech-bubble": {
    id: "speech-bubble",
    name: "Speech bubble",
    body: `
      <path d="M 10 8 L 90 8 Q 98 8 98 18 L 98 64 Q 98 74 88 74 L 38 74 L 18 96 L 26 74 L 12 74 Q 2 74 2 64 L 2 18 Q 2 8 10 8 Z"/>
    `,
  },
  sticker: {
    id: "sticker",
    name: "Sticker",
    body: `
      <g transform="rotate(-3 50 50)">
        <rect x="4" y="12" width="92" height="76" rx="9" ry="9"/>
      </g>
    `,
  },
};

/** Object-shape ids in display order for the picker. */
export const OBJECT_SHAPE_IDS: ContainerShape[] = [
  "glasses", "phone", "cupcake", "pizza", "computer",
  "bubble", "cloud", "house", "banner", "tag",
  "cup", "lightbulb",
  "burst", "speech-bubble", "sticker",
];

export function isObjectShape(id: ContainerShape): boolean {
  return id in CONTAINER_SHAPES;
}
